import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, AlertCircle } from 'lucide-react';
import { businessInfo } from '../mock';
import OverlayLetterheadRenderer from '../components/OverlayLetterheadRenderer';
import StructuredEstimateView from '../components/StructuredEstimateView';

const API = process.env.REACT_APP_BACKEND_URL;
// Served locally from /app/frontend/public so it always loads (no CORS / content-type quirks for print).
const LOGO_URL = '/logo-taylordarms.png';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};
const fmtDateOnly = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};

const usePageTitle = (title) => {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => { document.title = prev; };
  }, [title]);
};

const EstimateView = () => {
  const { id } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageTitle(estimate ? `Estimate ${estimate.estimate_number} — TayLord Arms LLC` : 'Estimate — TayLord Arms LLC');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/estimates/public/${id}`);
        if (!cancelled) setEstimate(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500" data-testid="estimate-loading">Loading estimate…</p>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="estimate-not-found">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-slate-900">Estimate not found</h1>
          <p className="text-slate-600 mt-2">This estimate may have been removed or the link is incorrect. Please contact TayLord Arms LLC if you believe this is an error.</p>
          <p className="text-slate-500 text-sm mt-4">{businessInfo.phone} · {businessInfo.email}</p>
        </div>
      </div>
    );
  }

  // ===== STRUCTURED LAYOUT (default for ALL estimates) =====
  // Older estimates without explicit layout='structured' also render in this design;
  // missing fields just show as empty lines on the form.
  return (
    <div className="min-h-screen bg-slate-100 py-6 print:py-0 print:bg-white">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.3in; }
          body { background: white !important; }
          .print-hide { display: none !important; }
        }
      `}</style>
      <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print-hide">
        <div className="text-sm text-slate-600">
          Estimate <span className="font-mono font-bold text-slate-900">{estimate.estimate_number}</span>
        </div>
        <Button data-testid="estimate-print" onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>
      <StructuredEstimateView estimate={estimate} />
    </div>
  );
};

export default EstimateView;
